"use client";

/**
 * An event's page taking its part in registering for it: a link's ?inscricao
 * opens the registration once the page has shown, and the query is taken off
 * the address with replaceState, keeping history.state. An event not open for
 * registration shows the page alone, with the query taken off: so does a client
 * with a registration on it already (paid or still to pay: one registration a
 * person, since Oct 2026), and anyone on an event with no places left -- the page
 * says so in place of the way to register.
 *
 * Followed again after the back and forward buttons, for a registration closed
 * and then gone forward to again (the registration adds a history entry with
 * {registration: id} as its state when it opens).
 */
import { useEffect } from "react";
import { REGISTRATION_PARAM } from "@/lib/address";
import type { EventWithState } from "@/lib/types/event";
import { openRegistration } from "@/components/registration/registration";
import { isSoldOutFor } from "@/components/event/labels";

const registrationInAddress = () => new URLSearchParams(window.location.search).has(REGISTRATION_PARAM);

const registrationInHistory = (id: string) => {
  const state = window.history.state as { registration?: unknown } | null;
  return !!state && String(state.registration || "") === id;
};

/** the address with the registration it asks for taken off */
export const withoutRegistrationParam = () => {
  const params = new URLSearchParams(window.location.search);
  params.delete(REGISTRATION_PARAM);
  const search = params.toString();
  return `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`;
};

/**
 * `ready` says the visitor's own state has been asked for: a client still to
 * pay must be known as one before the link is followed.
 */
export function RegistrationOpener({ event, ready = true }: { event: EventWithState; ready?: boolean }) {
  const id = String(event.id);
  const canOpen = event.registration_state === "OPEN" && !event.is_registered && !isSoldOutFor(event);

  useEffect(() => {
    if (!ready) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const follow = () => {
      if (!registrationInAddress() && !registrationInHistory(id)) {
        return;
      }
      if (!canOpen) {
        if (registrationInAddress()) {
          window.history.replaceState(window.history.state, "", withoutRegistrationParam());
        }
        return;
      }
      // once the page itself has been shown, so the registration is not left underneath it
      timer = setTimeout(() => {
        if (registrationInAddress()) {
          window.history.replaceState(window.history.state, "", withoutRegistrationParam());
        }
        openRegistration(Number(id));
      }, 50);
    };
    follow();
    const onPopState = () => {
      if (registrationInHistory(id)) {
        follow();
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("popstate", onPopState);
    };
  }, [id, canOpen, ready]);

  return null;
}
